import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { scrollTo } from 'utils/helpers/scroll';
import { useClassName, useResize } from 'common/hooks';
import { SVGIcon } from 'common/components';

import './tabs.less';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';

const OFFSET = 50;

const scrollTabIntoView = (container, tab) => {
  if (!container || !tab) return;

  const containerLeft = container.scrollLeft;
  const containerRight = containerLeft + container.offsetWidth;
  const tabLeft = tab.offsetLeft;
  const tabRight = tabLeft + tab.offsetWidth;

  if (tabLeft < containerLeft + OFFSET) {
    container.scrollTo({
      left: Math.max(0, tabLeft - OFFSET),
      behavior: 'smooth',
    });
  } else if (tabRight > containerRight - OFFSET) {
    container.scrollTo({
      left: tabRight - container.offsetWidth + OFFSET,
      behavior: 'smooth',
    });
  }
};

const useArrowControls = (containerRef, children) => {
  const [leftHidden, setLeftHidden] = useState(true);
  const [rightHidden, setRightHidden] = useState(true);
  const { width } = useResize(document.body);

  const updateControls = () => {
    if (!containerRef.current) return;

    const fullyVisible =
      containerRef.current.scrollWidth <= containerRef.current.offsetWidth + 1;

    setLeftHidden(containerRef.current.scrollLeft === 0);
    setRightHidden(
      fullyVisible ||
        containerRef.current.scrollLeft + containerRef.current.offsetWidth >
          containerRef.current.scrollWidth - OFFSET
    );
  };

  useEffect(() => {
    const onScroll = () => updateControls();

    containerRef.current.addEventListener('scroll', onScroll);

    return () => containerRef.current.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    updateControls();
  }, [width, children.length]);

  return { leftHidden, rightHidden };
};

const useWaitInitialScrollSize = (containerRef, children) => {
  const [scrollWidth, setScrollWidth] = useState(0);

  useLayoutEffect(() => {
    if (containerRef?.current?.children?.length === children.length) {
      const interval = setInterval(() => {
        setScrollWidth(containerRef.current?.scrollWidth);
      }, 100);

      return () => clearInterval(interval);
    }
  }, [containerRef?.current?.children?.length, children.length]);

  return [scrollWidth];
};

export const BaseTab = forwardRef(
  (
    {
      fixed,
      containerRef,
      children,
      selectedIndex,
      styles,
      tabsContainerStyles,
      arrowTabInverted,
    },
    ref
  ) => {
    const classNames = useClassName('Tabs');
    const { leftHidden, rightHidden } = useArrowControls(
      containerRef,
      children
    );
    const [scrollWidth] = useWaitInitialScrollSize(containerRef, children);

    useLayoutEffect(() => {
      if (selectedIndex !== undefined && selectedIndex !== null) {
        const tab = containerRef.current.children[selectedIndex];
        scrollTabIntoView(containerRef.current, tab);
      }
    }, [scrollWidth, selectedIndex]);

    const getChildrenOffsets = () =>
      Array.from(containerRef.current.children).map(
        (tab) => tab.offsetLeft - OFFSET
      );

    const scrollHorizontal = (offsetLeft) =>
      containerRef.current.scrollTo({ left: offsetLeft, behavior: 'smooth' });

    const getContainerClassnames = () => {
      if (!leftHidden && !rightHidden) return 'has-both-arrows';
      if (!leftHidden) return 'has-left-arrow';
      if (!rightHidden) return 'has-right-arrow';
      return '';
    };

    return (
      <div
        className={`${classNames('wrapper')} ${fixed && classNames('fixed')} ${
          styles?.container
        }`}
        ref={ref}>
        <SVGIcon
          name="angle-left"
          inverted={arrowTabInverted}
          onClick={() => {
            const offsets = getChildrenOffsets().reverse();
            offsets.shift();

            const offsetLeft = offsets.find(
              (offsetLeft) =>
                offsetLeft < containerRef.current.scrollLeft - OFFSET
            );

            scrollHorizontal(offsetLeft);
          }}
          className={`${classNames('left')} ${
            leftHidden ? classNames('hidden') : ''
          }`}
        />
        <div
          className={classNames('no-overflow', getContainerClassnames())}
          style={tabsContainerStyles}>
          <div
            className={`${classNames('container')} ${
              !leftHidden ? classNames('blur-left') : ''
            } ${!rightHidden ? classNames('blur-right') : ''}`}
            ref={containerRef}
            role="tablist">
            {children}
          </div>
        </div>
        <SVGIcon
          name="angle-right"
          inverted={arrowTabInverted}
          onClick={() => {
            const offsets = getChildrenOffsets();
            offsets.shift();

            const offsetLeft = offsets.find(
              (offsetLeft) =>
                offsetLeft > containerRef.current.scrollLeft + OFFSET
            );

            scrollHorizontal(offsetLeft);
          }}
          className={`${classNames('right')} ${
            rightHidden ? classNames('hidden') : ''
          }`}
        />
      </div>
    );
  }
);

const RefTabs = ({ names, fixed, refs, styles }) => {
  const classNames = useClassName('Tabs');
  const [selectedTab, setSelectedTab] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const selectedTab = refs.current.findIndex((a, index) => {
        if (index === 0 && scrollY < a.offsetTop) {
          return true;
        }

        if (a.offsetTop > scrollY) {
          return false;
        }

        if (refs.current.length === index + 1) return true;

        return scrollY < refs.current[index + 1].offsetTop;
      });

      if (names[selectedTab]) {
        setSelectedTab(selectedTab);
      }
    };

    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [names]);

  useEffect(() => {
    if (selectedTab === null) return;
    if (!refs.current[selectedTab]) return;

    const tab = containerRef.current.children[selectedTab];
    scrollTabIntoView(containerRef.current, tab);
  }, [selectedTab]);

  const onClickTab = (index) => {
    scrollTo(refs.current[index], -48);
  };

  return (
    <BaseTab fixed={fixed} containerRef={containerRef} styles={styles}>
      {names.map((name, index) => (
        <span
          className={`${index === selectedTab ? classNames('selected') : ''}`}
          key={index}
          onClick={() => onClickTab(index)}>
          {name}
        </span>
      ))}
    </BaseTab>
  );
};

const LinkTabs = ({
  links,
  isSelected,
  fixed,
  styles,
  onSelect = () => {},
}) => {
  const classNames = useClassName('Tabs');
  const containerRef = useRef(null);

  const selectedIndex = links.findIndex((link, index) => isSelected(index));
  const linkStyles = !styles?.link
    ? {}
    : typeof styles?.link === 'object'
    ? { ...styles?.link }
    : {};
  const activeLinkStyles = !styles?.activeLink
    ? {}
    : typeof styles?.activeLink === 'object'
    ? { ...styles?.activeLink }
    : {};
  const linkClassName = !styles?.linkClassName
    ? ''
    : typeof styles?.linkClassName === 'string'
    ? styles?.linkClassName
    : '';

  return (
    <BaseTab
      styles={styles}
      fixed={fixed}
      containerRef={containerRef}
      selectedIndex={selectedIndex}>
      {links.map((link, index) => (
        <Link
          to={link.url}
          onClick={(e) => onSelect(e, index)}
          key={index}
          className={`${
            isSelected(index) ? classNames('selected') : ''
          } ${linkClassName}`}
          style={{
            'font-weight': 'bold',
            ...linkStyles,
            ...(isSelected(index) ? activeLinkStyles : {}),
          }}>
          {link.title}
        </Link>
      ))}
    </BaseTab>
  );
};

const ControlledTabs = ({
  names,
  selectedIndex,
  onClick,
  fixed,
  styles,
  tabStyles,
  tabsContainerStyles,
  arrowTabInverted,
  handleTrack,
  renderTab,
  tabClassName,
}) => {
  const classNames = useClassName('Tabs');
  const containerRef = useRef(null);

  const selectedTabStyles = tabStyles
    ? Object.fromEntries(
        Object.entries(tabStyles)
          .filter(([key]) => key.startsWith('selected'))
          .map(([key, value]) => [key.replace('selected-', ''), value])
      )
    : {};

  const nonSelectedTabStyles = tabStyles
    ? Object.fromEntries(
        Object.entries(tabStyles).filter(([key]) => !key.startsWith('selected'))
      )
    : {};

  const handleClick = (name) => {
    if (handleTrack) {
      handleTrack({
        event: STANDARD_EVENTS.click,
        params: {
          label: name,
          cta_type: CTA_TYPES.tab,
        },
      });
    }
    onClick(name);
  };

  const handleKeyDown = (event, index) => {
    const lastIndex = names.length - 1;
    let nextIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = index === lastIndex ? 0 : index + 1;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = index === 0 ? lastIndex : index - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = lastIndex;
    }

    if (nextIndex === undefined) return;

    event.preventDefault();
    containerRef.current.children[nextIndex]?.focus();
    handleClick(names[nextIndex]);
  };

  return (
    <BaseTab
      fixed={fixed}
      containerRef={containerRef}
      styles={styles}
      selectedIndex={selectedIndex}
      tabsContainerStyles={tabsContainerStyles}
      arrowTabInverted={arrowTabInverted}>
      {names.map((name, index) => {
        const TabElement = renderTab ? 'button' : 'span';

        return (
          <TabElement
            {...(renderTab ? { type: 'button' } : {})}
            className={`${
              index === selectedIndex ? classNames('selected') : ''
            } ${tabClassName || ''}`}
            style={{
              ...nonSelectedTabStyles,
              ...(index === selectedIndex ? selectedTabStyles : {}),
            }}
            role="tab"
            aria-selected={index === selectedIndex}
            tabIndex={index === selectedIndex ? 0 : -1}
            key={name}
            onClick={() => handleClick(name)}
            onKeyDown={(event) => handleKeyDown(event, index)}>
            {renderTab ? renderTab(name, index) : name}
          </TabElement>
        );
      })}
    </BaseTab>
  );
};

const CustomTabs = forwardRef((props, ref) => {
  const {
    children,
    fixed,
    refs,
    styles,
    selectFirstWhenUnmatched = false,
  } = props;
  const classNames = useClassName('Tabs');
  const [selectedTab, setSelectedTab] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      const ref = refs.current.find((a, index) => {
        if (index === 0 && scrollY < a.offsetTop) {
          return true;
        }

        if (a.offsetTop > scrollY) {
          return false;
        }

        if (refs.current.length === index + 1) return true;

        return scrollY < refs.current[index + 1].offsetTop;
      });

      if (ref) setSelectedTab(ref.id);
    };

    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (selectedTab === null) return;

    const children = Array.from(containerRef.current.children);
    const childRef =
      children.find((child) => child.getAttribute('name') === selectedTab) ||
      (selectFirstWhenUnmatched ? children[0] : null);

    if (!childRef) return;

    scrollTabIntoView(containerRef.current, childRef);

    Array.from(containerRef.current.children).forEach((child) => {
      child.classList.remove(classNames('selected'));
    });
    childRef.classList.add(classNames('selected'));
  }, [selectedTab, selectFirstWhenUnmatched]);

  return (
    <BaseTab
      fixed={fixed}
      containerRef={containerRef}
      styles={styles}
      ref={ref}>
      {children}
    </BaseTab>
  );
});

LinkTabs.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
  isSelected: PropTypes.func.isRequired,
  fixed: PropTypes.bool,
};

RefTabs.propTypes = {
  names: PropTypes.arrayOf(PropTypes.string).isRequired,
  fixed: PropTypes.bool,
  refs: PropTypes.any,
};

BaseTab.propTypes = {
  fixed: PropTypes.bool,
  containerRef: PropTypes.object,
  selectedIndex: PropTypes.number,
  styles: PropTypes.object,
  customStyles: PropTypes.object,
  arrowTabInverted: PropTypes.bool,
};

BaseTab.defaultProps = {
  fixed: false,
  customStyles: {},
  arrowTabInverted: false,
};

export { LinkTabs, RefTabs, ControlledTabs, CustomTabs };
